<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import { useQueryGetCsmUsers } from '@/js/services/api/backOffice/backOfficeQueries';

    const { data: csmUsersData, isLoading } = useQueryGetCsmUsers();

    type Row = {
        tenantName: string;
        userId: string;
        email: string;
        name: string;
        roleName: string;
    };

    const rows = computed<Row[]>(() => {
        const entries = csmUsersData.value ?? {};
        return Object.entries(entries).flatMap(([tenantName, entry]) =>
            entry.tenantUsers.users.map((user) => ({
                tenantName,
                userId: user.id,
                email: user.email ?? '',
                name: [user.firstName, user.lastName].filter(Boolean).join(' '),
                roleName: user.role?.name ?? '',
            }))
        );
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-csm-users') }}</h1>
        </template>
        <template #content>
            <p class="mb-4 text-caption text-gray-medium">
                {{ __('back-office-csm-users-count', { count: String(rows.length) }) }}
            </p>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-gray-lightest">
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('back-office-users-col-tenant') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('users-table-column-name') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('email') }}
                            </th>
                            <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                                {{ __('back-office-users-col-role') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="4" class="py-12 text-center"><LoadingDots /></td>
                        </tr>
                        <template v-else>
                            <tr v-if="!rows.length">
                                <td colspan="4" class="py-6 text-body text-gray-medium text-center">
                                    {{ __('back-office-csm-users-empty') }}
                                </td>
                            </tr>
                            <tr
                                v-for="row in rows"
                                :key="row.userId"
                                class="border-b border-gray-lightest"
                            >
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.tenantName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.name }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.email }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.roleName }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </SettingsPageLayout>
</template>
